import React, { useEffect, useMemo, useRef } from 'react';

import { useForm } from 'react-hook-form';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import FiltersDropdown from 'components/common/GlobalFiltersInPage/components/FiltersDropdown/FiltersDropdown';
import { createInitialFiltersStatusRef } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { useGetDropdownOptions } from 'components/common/GlobalFiltersInPage/hooks/useGetDropdownOptions';
import { useGetPageFiltersValue } from 'components/common/GlobalFiltersInPage/hooks/useGetPageFiltersValue';
import { useGetVersionIdAndCompareId } from 'components/common/GlobalFiltersInPage/hooks/useGetVersionIdAndCompareId';
import { useQueryStringAndStoreHook } from 'components/common/GlobalFiltersInPage/hooks/useQueryStringAndStoreHook';
import {
    SingleFilterReadyPayload,
    useOnSingleFilterReady,
} from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { Button } from 'components/DesignSystem/Button';

interface Props {
    children: React.ReactNode;
    defaultValues: any;
    clearFiltersValue?: any;
    dropdownFiltersKeys?: string[];
}

export interface FiltersStatus {
    [key: string]: {
        isReady: boolean;
    };
}

export const GlobalFiltersInPage = ({
    children,
    defaultValues,
    dropdownFiltersKeys = [],
    clearFiltersValue,
}: Props) => {
    const { versionId, compareVersionId } = useGetVersionIdAndCompareId();
    const { dropdownFiltersOptions, setDropdownFiltersOptions } = useGetDropdownOptions({
        dropdownFiltersKeys,
    });
    const { project, contract } = useProjectTreeStore();
    const { setDefaultValues } = useGlobalFilterDataStore();
    const totalFilters = useMemo(
        () => React.Children.toArray(children).filter((child) => React.isValidElement(child) && child.props.name),
        [children],
    );
    const filtersStatusRef = useRef<FiltersStatus>(createInitialFiltersStatusRef(totalFilters));
    const filtersMapValues = useGetPageFiltersValue({ defaultValues });
    const methods = useForm({ defaultValues: filtersMapValues });
    const { handleSubmit, watch, reset } = methods;
    const toggleGlobalFilterComponent = useGlobalFilterDataStore((store) => store.toggleGlobalFilterComponent);
    useEffect(() => {
        setDefaultValues(defaultValues);
    }, []);

    useOnSingleFilterReady(({ filterId, isReady }: SingleFilterReadyPayload) => {
        filtersStatusRef.current[filterId] = {
            isReady,
        };

        const allFilters = Object.keys(filtersStatusRef.current);
        const numberOfReadyFields = allFilters.filter((key) => filtersStatusRef.current[key]?.isReady).length;
        const isAllFiltersReady = numberOfReadyFields === allFilters.length;

        if (isAllFiltersReady) {
            updateZustandAndQueryParams(watch());
        }
    });

    const handleClearAll = () => {
        reset(clearFiltersValue ?? defaultValues);
        updateZustandAndQueryParams(clearFiltersValue ?? defaultValues);
    };

    const handleFilters = () => {
        updateZustandAndQueryParams(watch());
    };
    const [setValueQueryStringAndZustandValue] = useQueryStringAndStoreHook();
    const updateZustandAndQueryParams = (value) => {
        setValueQueryStringAndZustandValue(value);
    };

    return toggleGlobalFilterComponent && filtersStatusRef.current ? (
        <div data-testid={'global-filter-component'} className={classes.container}>
            <div className={classes.form}>
                <form
                    onSubmit={handleSubmit(updateZustandAndQueryParams)}
                    className={classes.globalFilterComponentContainer}
                >
                    {React.Children.map(children, (child) => {
                        const { name } = child.props;
                        const filterIsSelectByDefault = dropdownFiltersOptions[name]?.isSelected === undefined;
                        const dropdownOptionBeenSelected = dropdownFiltersOptions[name]?.isSelected;
                        const isSelected = filterIsSelectByDefault || dropdownOptionBeenSelected;

                        return React.isValidElement(child) && child.props.name
                            ? React.createElement(child.type, {
                                  ...{
                                      ...child.props,
                                      methods: methods,
                                      key: child.props.name,
                                      filtersStatusRef: filtersStatusRef,
                                      handleFilters: handleFilters,
                                      value: watch(child.props.name),
                                      isSelected,
                                      versionId,
                                      compareVersionId,
                                      project,
                                      contract,
                                  },
                              })
                            : child;
                    })}
                </form>
            </div>

            <div data-testid={'actions'} className={classes.actions}>
                {dropdownFiltersKeys?.length > 0 && (
                    <FiltersDropdown
                        selectedFiltersOptions={dropdownFiltersOptions}
                        setSelectedFiltersOptions={setDropdownFiltersOptions}
                        methods={methods}
                        handleFilters={handleFilters}
                    />
                )}
                <div>
                    <Button
                        data-testid={'clear-filters'}
                        startIcon={
                            <MuiIcon
                                className={classes.deleteIcon}
                                icon={'delete'}
                                fontSize={'2rem'}
                                type={'outlined'}
                            />
                        }
                        variant={'secondary'}
                        onClick={handleClearAll}
                        className={classes.clearButton}
                    >
                        <span className={classes.clearFiltersText}>Clear Filters</span>
                    </Button>
                </div>
            </div>
        </div>
    ) : null;
};
